import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import GridSectionLoader from '../../components/CustomComponents/preLoad/gridSectionLoader';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import useThemeColor from '../../hooks/useThemeColor';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import moment from 'moment';
import Select from 'react-select';
import useDarkMode from '../../hooks/useDarkMode';
import DropDownStyles, { DropDownStylesDark, defaultSelectPortalProps } from '../staff/dropdownStyles';
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from '../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar';
import { getModernGridFilterPillSelectStyles } from '../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';

const StaffWorkReport = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    summary: {},
    staffReports: []
  });
  const [allStaffList, setAllStaffList] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf('day').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
  const [selectedStaff, setSelectedStaff] = useState({ value: '', label: 'All Staff' });
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  useEffect(() => {
    fetchStaffList();
  }, []);

  useDebouncedEffect(() => {
    fetchStaffWorkReport();
  }, [startDate, endDate, selectedStaff]);

  const fetchStaffList = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: { 
          CompID,
          SortBy: 'First_Name',
          SortOrder: 'ASC',
          Search: '',
          PageSize: 1000,
          PageNumber: 1
        }
      });
      
      const staffData = response.data.data || response.data || [];
      if (Array.isArray(staffData)) {
        setAllStaffList(staffData);
      } else {
        setAllStaffList([]);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff list');
      console.error('Error fetching staff list:', err);
      setAllStaffList([]);
    }
  };

  const fetchStaffWorkReport = async () => {
    if (!CompID) return;

    setLoading(true);
    try {
      const params = {
        compID: CompID,
        startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
        endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
      };

      if (selectedStaff?.value) {
        params.userName = selectedStaff.value;
      }

      const response = await axiosInstance.get(APIs.GET_STAFF_SALES_REPORT, {
        params: params
      });

      if (response.data) {
        setReportData({
          summary: response.data.summary || {},
          staffReports: response.data.staffReports || []
        });
      } else {
        setReportData({
          summary: {},
          staffReports: []
        });
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff work report');
      console.error('Error fetching staff work report:', err);
      setReportData({
        summary: {},
        staffReports: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    } else {
      showErrorToast('Invalid date selected');
    }
  };

  const handleStaffChange = (selectedOption) => {
    setSelectedStaff(selectedOption);
  };

  const formatCurrency = (amount) => {
    try {
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      console.error('Currency formatting error:', error);
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };

  const staffOptions = React.useMemo(() => {
    const options = [
      { value: '', label: 'All Staff' },
      ...allStaffList.map(staff => ({
        value: staff.userName || staff.UserName,
        label: `${staff.firstName || ''} ${staff.lastName || ''}`.trim() || staff.email || staff.userName || 'Unknown'
      }))
    ];
    return options;
  }, [allStaffList]);

  // Get category sales as array for rendering
  const getCategorySales = (categorySales) => {
    if (!categorySales || typeof categorySales !== 'object') return [];
    return Object.values(categorySales);
  };

  // Summary cards data
  const summary = reportData.summary || {};
  const categorySales = getCategorySales(summary.categorySales);

  return (
    <div>
      {/* Breadcrumb */}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/report">Reports</a></li>
        <li className="breadcrumb-item active">Staff Work Report</li>
      </ol>
      <h1 className="page-header">Staff Work Report</h1>

      <div className="mb-4" style={{ position: 'relative', zIndex: 100, overflow: 'visible' }}>
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Staff Work Report"
          totalCount={reportData.staffReports.length}
          topFilters={
            <>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={startDate && moment(startDate).isValid() ? startDate : null}
                  endDate={endDate && moment(endDate).isValid() ? endDate : null}
                  onChange={handleDateRangeChange}
                  placeholder="Select Date Range"
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-user">
                <Select
                  value={selectedStaff}
                  onChange={handleStaffChange}
                  options={staffOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Staff"
                  isSearchable
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            </>
          }
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="320px">
      {/* Summary Cards - Compact Single Row Design */}
      <div className="mb-4" style={{ position: 'relative', zIndex: 0, isolation: 'isolate' }}>
        <div className="d-flex gap-3 flex-wrap">
          {/* Total Sales */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-dollar-sign" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalSales || 0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Sales</div>
            </div>
          </div>

          {/* Total Orders */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-shopping-cart" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{summary.totalOrders || 0}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Orders</div>
            </div>
          </div>

          {/* Total Tips */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-money-bill-wave" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalTips || 0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Tips</div>
            </div>
          </div>

          {/* Total Refunds */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-undo" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalRefunds || 0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Refunds</div>
            </div>
          </div>

          {/* Total Voids */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-ban" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{summary.totalVoids || 0}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Voids</div>
            </div>
          </div>

          {/* Total Discounts */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-percentage" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(summary.totalDiscounts || 0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Discounts</div>
            </div>
          </div>

          {/* Total Wet */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-tint" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Wet</div>
            </div>
          </div>

          {/* Total Alcohol */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-wine-glass" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Alcohol</div>
            </div>
          </div>

          {/* Total Sides */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-utensils" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total Sides</div>
            </div>
          </div>

          {/* Total FOOD */}
          <div 
            className="flex-grow-1" 
            style={{ 
              minWidth: '150px',
              backgroundColor: themeColor,
              borderRadius: '8px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'white'
            }}
          >
            <i className="fa fa-burger" style={{ fontSize: '24px', opacity: 0.9 }}></i>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(0)}</div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>Total FOOD</div>
            </div>
          </div>

          {/* Dynamic Category Sales Cards */}
          {categorySales.map((category) => (
            <div 
              key={category.categoryId}
              className="flex-grow-1" 
              style={{ 
                minWidth: '150px',
                backgroundColor: themeColor,
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'white'
              }}
            >
              <i className="fa fa-chart-pie" style={{ fontSize: '24px', opacity: 0.9 }}></i>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(category.saleAmount || 0)}</div>
                <div style={{ fontSize: '12px', opacity: 0.85 }}>Total {category.categoryName}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Staff Report Table */}
      <div className="card border-0" style={{ position: 'relative', zIndex: 1, isolation: 'isolate' }}>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ position: 'relative', zIndex: 1 }}>
            <table className="table table-hover mb-0">
              <thead 
                className="bg-light" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 3,
                  backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa'
                }}
              >
                <tr>
                  <th className="pt-3 pb-3">Staff Member</th>
                  <th className="pt-3 pb-3 text-center">Login Time</th>
                  <th className="pt-3 pb-3 text-end">Sale Amount</th>
                  <th className="pt-3 pb-3 text-center">Order Count</th>
                  <th className="pt-3 pb-3 text-end">Tip</th>
                  <th className="pt-3 pb-3 text-end">Refund</th>
                  <th className="pt-3 pb-3">Refund Reason</th>
                  <th className="pt-3 pb-3 text-center">Void Count</th>
                  <th className="pt-3 pb-3 text-end">Discounts</th>
                  {categorySales.map(category => (
                    <th key={category.categoryId} className="pt-3 pb-3 text-end">{category.categoryName} Sale</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.staffReports.length === 0 ? (
                  <tr>
                    <td colSpan={9 + categorySales.length} className="text-center py-5">
                      <i className="fa fa-users fa-3x text-muted mb-3 d-block"></i>
                      <h5>No Staff Work Reports Found</h5>
                      <p className="text-muted">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  reportData.staffReports.map((staff) => {
                    const staffCategorySales = getCategorySales(staff.categorySales);
                    return (
                      <tr key={staff.userName}>
                        <td className="align-middle">
                          <div className="d-flex align-items-center">
                            <div className="w-40px h-40px rounded-circle bg-light d-flex align-items-center justify-content-center me-3">
                              <i className="fa fa-user text-muted"></i>
                            </div>
                            <div>
                              <div className="fw-bold">
                                {staff.firstName && staff.lastName 
                                  ? `${staff.firstName} ${staff.lastName}` 
                                  : staff.userName || 'N/A'}
                              </div>
                              <div className="text-muted small">{staff.roleName || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="align-middle text-center">
                          <span className="badge bg-info">{staff.loginTime}</span>
                        </td>
                        <td className="align-middle text-end fw-bold">
                          {formatCurrency(staff.saleAmount || 0)}
                        </td>
                        <td className="align-middle text-center">
                          <span className="badge bg-primary">{staff.orderCount || 0}</span>
                        </td>
                        <td className="align-middle text-end">
                          {formatCurrency(staff.tip || 0)}
                        </td>
                        <td className="align-middle text-end">
                          <span className={staff.refund > 0 ? 'text-danger fw-semibold' : ''}>
                            {formatCurrency(staff.refund || 0)}
                          </span>
                        </td>
                        <td className="align-middle">
                          <span className="small text-muted">{staff.refundReason || '-'}</span>
                        </td>
                        <td className="align-middle text-center">
                          <span className={`badge ${staff.voidCount > 0 ? 'bg-danger' : 'bg-secondary'}`}>
                            {staff.voidCount || 0}
                          </span>
                        </td>
                        <td className="align-middle text-end">
                          {formatCurrency(staff.discounts || 0)}
                        </td>
                        {categorySales.map(summaryCategory => {
                          const staffCategory = staffCategorySales.find(c => c.categoryName === summaryCategory.categoryName);
                          return (
                            <td key={summaryCategory.categoryId} className="align-middle text-end">
                              {formatCurrency(staffCategory?.saleAmount || 0)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </GridSectionLoader>
    </div>
  );
};

export default StaffWorkReport;

